import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useUsers } from "../hooks/useUsers";
import { UserTable } from "../components/UserTable";
import { Input, Select } from "@/shared/ui";
import { useConfirm } from "@/hooks/useConfirm";

export const UsersManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const { confirm, Modal: ConfirmModal } = useConfirm();
  const {
    users,
    total,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    updatePagination,
    suspendUser,
    activateUser,
    refetch,
  } = useUsers();

  const handleSuspendUser = async (userId: string) => {
    const confirmed = await confirm({
      title: t("admin.users.confirmSuspendTitle"),
      message: t("admin.users.confirmSuspendMessage"),
      confirmText: t("admin.users.suspend"),
      variant: "danger",
    });
    if (confirmed) suspendUser(userId);
  };

  const handleActivateUser = async (userId: string) => {
    const confirmed = await confirm({
      title: t("admin.users.confirmActivateTitle"),
      message: t("admin.users.confirmActivateMessage"),
      confirmText: t("admin.users.activate"),
      variant: "warning",
    });
    if (confirmed) activateUser(userId);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilters({ search: searchTerm });
  };

  const handleFilterChange = (key: string, value: any) => {
    updateFilters({ [key]: value === "all" ? undefined : value });
  };

  const totalPages = Math.ceil(total / (pagination.pageSize || 50));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-juridico">
          {t("admin.users.title")}
        </h1>
        <p className="text-juridico/60 mt-1">{t("admin.users.subtitle")}</p>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="lg:col-span-2">
            <div className="relative">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                placeholder={t("admin.users.filters.search")}
                leftIcon={
                  <MagnifyingGlassIcon className="w-5 h-5 text-tiber-900/40" />
                }
              />
            </div>
          </form>

          {/* User Type Filter */}
          <Select
            value={filters.user_type || "all"}
            onChange={(e) => handleFilterChange("user_type", e.target.value)}
            options={[
              { value: "all", label: t("admin.users.filters.allTypes") },
              { value: "lawyer", label: t("admin.users.filters.lawyer") },
              { value: "firm", label: t("admin.users.filters.firm") },
              { value: "client", label: t("admin.users.filters.client") },
            ]}
          />

          {/* Status Filter */}
          <Select
            value={
              filters.is_active === undefined
                ? "all"
                : filters.is_active
                  ? "active"
                  : "suspended"
            }
            onChange={(e) =>
              handleFilterChange(
                "is_active",
                e.target.value === "all"
                  ? undefined
                  : e.target.value === "active"
              )
            }
            options={[
              { value: "all", label: t("admin.users.filters.allStatus") },
              { value: "active", label: t("admin.users.filters.active") },
              { value: "suspended", label: t("admin.users.filters.suspended") },
            ]}
          />
        </div>

        {/* Results count */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-juridico/60">
            {t("admin.users.showing", { count: users.length, total })}
          </p>
          <button
            onClick={refetch}
            className="text-sm text-tiber-500 hover:text-tiber-500/80 font-medium"
          >
            {t("common.refresh")}
          </button>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg border border-romance-200 overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-juridico/60">{t("admin.users.noResults")}</p>
          </div>
        ) : (
          <UserTable
            users={users}
            onSuspend={handleSuspendUser}
            onActivate={handleActivateUser}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-romance-200 flex items-center justify-between">
            <button
              onClick={() =>
                updatePagination({ page: (pagination.page || 1) - 1 })
              }
              disabled={pagination.page === 1}
              className="px-4 py-2 text-sm font-medium text-juridico bg-white border border-romance-200 rounded-lg hover:bg-romance-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.previous")}
            </button>
            <span className="text-sm text-juridico">
              {t("common.pageOfPages", {
                current: pagination.page,
                total: totalPages,
              })}
            </span>
            <button
              onClick={() =>
                updatePagination({ page: (pagination.page || 1) + 1 })
              }
              disabled={pagination.page === totalPages}
              className="px-4 py-2 text-sm font-medium text-juridico bg-white border border-romance-200 rounded-lg hover:bg-romance-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </motion.div>

      {ConfirmModal}
    </div>
  );
};
